/**
 * Shared User Areas Service
 * 
 * Generic API service for user-area assignments.
 * Used by campaigns, devices, and other applications.
 */

import { apiClient } from './api.service';

// =============================================================================
// TYPES
// =============================================================================

export interface UserArea {
  id: number;
  userId: number;
  areaId: number;
  appId?: number;
  user?: { id: number; fullName: string; email: string };
  area?: { areaId?: number; id?: number; areaName: string };
}

// =============================================================================
// USER AREAS SERVICE FACTORY
// =============================================================================

/**
 * Creates a user-areas service for a specific endpoint base.
 * 
 * @example
 * // For campaigns app
 * const campaignUserAreasService = createUserAreasService('campaign-user-areas');
 * 
 * @example
 * // For devices app
 * const deviceUserAreasService = createUserAreasService('user-areas');
 */
export function createUserAreasService(basePath: string = 'user-areas') {
  const baseUrl = `/${basePath}`;

  return {
    /**
     * Get all user-area assignments
     */
    async getAll(): Promise<UserArea[]> {
      const response = await apiClient.get(baseUrl);
      const data = response.data?.data || response.data;
      return Array.isArray(data) ? data : [];
    },

    /**
     * Get areas for a specific user
     */
    async getByUser(userId: number): Promise<UserArea[]> {
      const response = await apiClient.get(`${baseUrl}/user/${userId}`);
      const data = response.data?.data || response.data;
      return Array.isArray(data) ? data : [];
    },

    /**
     * Get users for a specific area
     */
    async getByArea(areaId: number): Promise<UserArea[]> {
      const response = await apiClient.get(`${baseUrl}/area/${areaId}`);
      const data = response.data?.data || response.data;
      return Array.isArray(data) ? data : [];
    },

    /**
     * Add areas to a user
     */
    async addAreasToUser(userId: number, areaIds: number[]): Promise<UserArea[]> {
      const response = await apiClient.post(`${baseUrl}/add-areas`, {
        userId,
        areaIds,
      });
      const data = response.data?.data || response.data;
      return Array.isArray(data) ? data : [];
    },

    /**
     * Remove areas from a user
     */
    async removeAreasFromUser(userId: number, areaIds: number[]): Promise<void> {
      await apiClient.post(`${baseUrl}/remove-areas`, {
        userId,
        areaIds,
      });
    },

    /**
     * Remove all areas from a user
     */
    async removeAllFromUser(userId: number): Promise<void> {
      await apiClient.delete(`${baseUrl}/user/${userId}/all`);
    },

    /**
     * Delete a single assignment
     */
    async delete(id: number): Promise<void> {
      await apiClient.delete(`${baseUrl}/${id}`);
    },
  };
}

// =============================================================================
// PRE-CONFIGURED SERVICE INSTANCES
// =============================================================================

/**
 * Default user-areas service (for devices management)
 */
export const userAreasService = createUserAreasService('user-areas');

/**
 * Campaign user-areas service
 */
export const campaignUserAreasService = createUserAreasService('campaign-user-areas');

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type UserAreasService = ReturnType<typeof createUserAreasService>;
